import API from "../../core/api/typed/api";
import {
  OnlineInvitationSite,
  OnlineInvitationSiteDetails,
  Site,
  WeddingSite,
  WeddingWebsiteSiteDetails,
} from "../../resources/modelTypes";
import createModelContext, { ModelContextValue } from "../createModelContext";
import React, { memo, ReactNode, useCallback } from "react";

// themeDataOverrides is a freeform JSON blob whose nested keys (e.g. "LargeHeadline",
// "fontFamily") must not be snake_cased or camelCased by the API client.
// handleDelete keeps the site in state with status "deleted" rather than removing it,
// matching the backend's soft-delete behaviour (HTTP 204, no body).
const SiteModelContext = createModelContext<Site>("Site", {
  ignorePaths: ["themeDataOverrides"],
  onRemove: (id, setServerItems) => {
    setServerItems((prev) =>
      prev.map((site) =>
        site.id === id ? { ...site, status: "deleted" } : site,
      ),
    );
  },
});

/** Provides site data and CRUD operations for the current session. */
const SiteProviderComponent = ({
  children,
  initialItems,
}: {
  children: ReactNode;
  initialItems: Site[];
}) => (
  <SiteModelContext.Provider initialItems={initialItems}>
    {children}
  </SiteModelContext.Provider>
);

export const SiteProvider = memo(SiteProviderComponent);

export type SiteContextValue = ModelContextValue<Site> & {
  /** Posts to the custom set_asset endpoint and updates local context state with the returned site. */
  setSiteAsset: (key: string, imageUrl: string) => Promise<Site>;
  /** PUTs notification settings and updates local context state without a full site refetch. */
  updateNotificationSettings: (
    settings: Record<string, boolean>,
  ) => Promise<void>;
  /**
   * Stages an update to one or more siteDetails fields. Automatically spreads
   * the current siteDetails so previous staged changes are not lost.
   */
  stageSiteDetailsField: (
    update: Partial<WeddingWebsiteSiteDetails | OnlineInvitationSiteDetails>,
  ) => void;
};

/** Returns the full site context: items, isLoading, CRUD, staged/commit/discard, and site-specific helpers. */
export const useSites = (): SiteContextValue => {
  const context = SiteModelContext.useModel();
  const { items, replaceItem, stage } = context;
  const site = items[0];

  const setSiteAsset = useCallback(
    (key: string, imageUrl: string): Promise<Site> =>
      API.post<Site>(
        `sites/${site.id}/set_asset`,
        {},
        { key, value: imageUrl },
      ).then((updatedSite) => {
        replaceItem(updatedSite);
        return updatedSite;
      }),
    [site.id, replaceItem],
  );

  const updateNotificationSettings = useCallback(
    (settings: Record<string, boolean>): Promise<void> =>
      API.put("notification-settings", {}, settings).then(() => {
        replaceItem({ ...site, notificationSettings: settings } as Site);
      }),
    [site, replaceItem],
  );

  const stageSiteDetailsField = useCallback(
    (
      update: Partial<WeddingWebsiteSiteDetails | OnlineInvitationSiteDetails>,
    ): void => {
      stage({
        id: site.id,
        siteDetails: {
          ...site.siteDetails,
          ...update,
        },
      } as Partial<Site>);
    },
    [site.id, site.siteDetails, stage],
  );

  return {
    ...context,
    setSiteAsset,
    stageSiteDetailsField,
    updateNotificationSettings,
  };
};

/** Returns the single site for this session (items[0]). */
export const useCurrentSite = (): WeddingSite | OnlineInvitationSite => {
  const { items } = SiteModelContext.useModel();
  return items[0] as WeddingSite | OnlineInvitationSite;
};
